import React from 'react'
import { Route, Routes } from 'react-router-dom'
import { LoginPage } from '../domain/guest/login/LoginPage'
import { ProtectedArea } from '../auth'
import { ProjectsOverviewPage } from '../domain/project/ProjectsOverviewPage'
import { ProjectDetailPage } from '../domain/project/ProjectDetailPage'
import { HomePage } from '../domain/home/HomePage'
import { TimeTrackingOverviewPage } from '../domain/timeTracking/TimeTrackingOverviewPage'
import { TimeTrackingEditPage } from '../domain/timeTracking/TimeTrackingEditPage'
import { NotFoundError } from '../layouts/Error'
import { useHasModule, useHasModulePermission } from '../auth/hooks'
import { Module, Permission } from '../auth/types'
import { AddressesOverviewPage } from '../domain/address/AddressesOverviewPage'
import { AddressDetailPage } from '../domain/address/AddressDetailPage'
import { AddressEditPage } from '../domain/address/AddressEditPage'
import { AddressAddPage } from '../domain/address/AddressAddPage'
import { AddressNotificationAddPage } from '../domain/address/AddressNotificationAddPage'
import { AddressNotificationDetailPage } from '../domain/address/AddressNotificationDetailPage'
import { AddressNotificationEditPage } from '../domain/address/AddressNotificationEditPage'
import { ActivityDetailPage } from '../domain/activity/ActivityDetailPage'
import { ActivityEditPage } from '../domain/activity/ActivityEditPage'
import { ActivitySignaturePage } from '../domain/activity/ActivitySignaturePage'
import { ActivityAddPage } from '../domain/activity/ActivityAddPage'
import { ActivityNotificationAddPage } from '../domain/activity/ActivityNotificationAddPage'
import { ActivityNotificationDetailPage } from '../domain/activity/ActivityNotificationDetailPage'
import { ActivityNotificationEditPage } from '../domain/activity/ActivityNotificationEditPage'
import { ProjectEditPage } from '../domain/project/ProjectEditPage'
import { ProjectNotificationAddPage } from '../domain/project/ProjectNotificationAddPage'
import { ProjectNotificationDetailPage } from '../domain/project/ProjectNotificationDetailPage'
import { ProjectNotificationEditPage } from '../domain/project/ProjectNotificationEditPage'
import { DashboardOverviewPage } from '../domain/dashboard/DashboardOverviewPage'
import { ActivitiesOverviewGroupPage } from '../domain/activity/ActivitiesOverviewGroupPage'
import ProfilePage from '../domain/profile/ProfilePage'
import ProfileEditPage from '../domain/profile/edit/ProfileEditPage'

type ProtectedModuleRouteProps = {
  module: Module
}

type ProtectedModulePermissionRouteProps = {
  module: Module
  permission: Permission
}

const ProtectedModuleRoute: React.FC<ProtectedModuleRouteProps> = ({
  module,
  children,
}) => {
  const hasModule = useHasModule(module)

  return hasModule ? <>{children}</> : <NotFoundError />
}

const ProtectedModulePermissionRoute: React.FC<
  ProtectedModulePermissionRouteProps
> = ({ module, permission, children }) => {
  const hasModulePermission = useHasModulePermission(module, permission)

  return hasModulePermission ? <>{children}</> : <NotFoundError />
}

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="*"
        element={
          <ProtectedArea>
            <Routes>
              <Route path="/dashboard" element={<DashboardOverviewPage />} />
              <Route path="/projects" element={<ProjectsOverviewPage />} />
              <Route path="/projects/:id" element={<ProjectDetailPage />} />
              <Route path="/projects/:id/edit" element={<ProjectEditPage />} />
              <Route
                path="/projects/:id/notifications/add"
                element={<ProjectNotificationAddPage />}
              />
              <Route
                path="/projects/:id/notifications/:notificationId"
                element={<ProjectNotificationDetailPage />}
              />
              <Route
                path="/projects/:id/notifications/:notificationId/edit"
                element={<ProjectNotificationEditPage />}
              />
              <Route
                path="/time-tracking"
                element={
                  <ProtectedModuleRoute module={Module.timeTacking}>
                    <TimeTrackingOverviewPage />
                  </ProtectedModuleRoute>
                }
              />
              <Route
                path="/time-tracking/:id/edit"
                element={
                  <ProtectedModulePermissionRoute
                    module={Module.timeTacking}
                    permission={Permission.edit}
                  >
                    <TimeTrackingEditPage />
                  </ProtectedModulePermissionRoute>
                }
              />
              <Route
                path="/addresses"
                element={
                  <ProtectedModuleRoute module={Module.addresses}>
                    <AddressesOverviewPage />
                  </ProtectedModuleRoute>
                }
              />
              <Route
                path="/addresses/add"
                element={
                  <ProtectedModuleRoute module={Module.addresses}>
                    <AddressAddPage />
                  </ProtectedModuleRoute>
                }
              />
              <Route
                path="/addresses/:id"
                element={
                  <ProtectedModuleRoute module={Module.addresses}>
                    <AddressDetailPage />
                  </ProtectedModuleRoute>
                }
              />
              <Route
                path="/addresses/:id/edit"
                element={
                  <ProtectedModuleRoute module={Module.addresses}>
                    <AddressEditPage />
                  </ProtectedModuleRoute>
                }
              />
              <Route
                path="/addresses/:id/notifications/add"
                element={<AddressNotificationAddPage />}
              />
              <Route
                path="/addresses/:id/notifications/:notificationId"
                element={<AddressNotificationDetailPage />}
              />
              <Route
                path="/addresses/:id/notifications/:notificationId/edit"
                element={<AddressNotificationEditPage />}
              />
              <Route
                path="/activity-groups/:id"
                element={
                  <ProtectedModuleRoute module={Module.activities}>
                    <ActivitiesOverviewGroupPage />
                  </ProtectedModuleRoute>
                }
              />
              <Route
                path="/activity-groups/:activityGroupId/activities/add"
                element={
                  <ProtectedModuleRoute module={Module.activities}>
                    <ActivityAddPage />
                  </ProtectedModuleRoute>
                }
              />
              <Route
                path="/activity-groups/:activityGroupId/activities/:activityId"
                element={
                  <ProtectedModuleRoute module={Module.activities}>
                    <ActivityDetailPage />
                  </ProtectedModuleRoute>
                }
              />
              <Route
                path="/activity-groups/:activityGroupId/activities/:activityId/sign"
                element={
                  <ProtectedModuleRoute module={Module.activities}>
                    <ActivitySignaturePage />
                  </ProtectedModuleRoute>
                }
              />
              <Route
                path="/activity-groups/:activityGroupId/activities/:activityId/edit"
                element={
                  <ProtectedModuleRoute module={Module.activities}>
                    <ActivityEditPage />
                  </ProtectedModuleRoute>
                }
              />
              <Route
                path="/activity-groups/:activityGroupId/activities/:activityId/notifications/add"
                element={<ActivityNotificationAddPage />}
              />
              <Route
                path="/activity-groups/:activityGroupId/activities/:activityId/notifications/:notificationId"
                element={<ActivityNotificationDetailPage />}
              />
              <Route
                path="/activity-groups/:activityGroupId/activities/:activityId/notifications/:notificationId/edit"
                element={<ActivityNotificationEditPage />}
              />
              <Route path={'/profile'} element={<ProfilePage />} />
              <Route path={'/profile/edit'} element={<ProfileEditPage />} />
              <Route path="/" element={<HomePage />} />
              <Route path="*" element={<NotFoundError />} />
            </Routes>
          </ProtectedArea>
        }
      />
    </Routes>
  )
}

export { AppRoutes }
